import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  FileText,
  Eye,
  Download,
  Send,
  CheckCircle,
  DollarSign,
  Calendar,
  User,
  Mail,
  ExternalLink,
  X,
  Copy,
  Loader2,
  MessageSquare,
  Phone,
  CreditCard,
  Edit,
  Trash2,
  Bell,
} from 'lucide-react';
import { format } from 'date-fns';
import { pdf } from '@react-pdf/renderer';
import ContractTemplate from '../../components/contracts/ContractTemplate';
import {
  getContractsWithStats,
  updateContractStatus,
  deleteContract,
  CONTRACT_STATUS,
} from '../../services/contractService';
import { sendContractSMS, createPaymentLink } from '../../services/apiService';
import { db } from '../../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';

// Contract lifecycle progress bar component
function ContractProgressBar({ status }) {
  const stages = [
    { key: 'draft', label: 'Draft', icon: FileText },
    { key: 'sent', label: 'Sent', icon: Send },
    { key: 'viewed', label: 'Viewed', icon: Eye },
    { key: 'signed', label: 'Signed', icon: CheckCircle },
    { key: 'deposit_paid', label: 'Deposit', icon: CreditCard },
    { key: 'paid', label: 'Paid', icon: DollarSign },
  ];

  const currentIndex = stages.findIndex((s) => s.key === status);

  return (
    <div className="flex items-center gap-1 mt-3">
      {stages.map((stage, index) => {
        const Icon = stage.icon;
        const isCompleted = index <= currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <div key={stage.key} className="flex items-center">
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${
                isCompleted
                  ? isCurrent
                    ? 'bg-orange-500 text-white'
                    : 'bg-green-500/20 text-green-400'
                  : 'bg-gray-800 text-gray-500'
              }`}
            >
              <Icon className="w-3 h-3" />
              <span className="hidden sm:inline">{stage.label}</span>
            </div>
            {index < stages.length - 1 && (
              <div
                className={`w-4 h-0.5 mx-0.5 ${
                  index < currentIndex ? 'bg-green-500' : 'bg-gray-700'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const statusConfig = {
  [CONTRACT_STATUS.DRAFT]: {
    label: 'Draft',
    color: 'bg-gray-500',
    icon: FileText,
  },
  [CONTRACT_STATUS.SENT]: {
    label: 'Sent',
    color: 'bg-blue-500',
    icon: Send,
  },
  [CONTRACT_STATUS.VIEWED]: {
    label: 'Viewed',
    color: 'bg-yellow-500',
    icon: Eye,
  },
  [CONTRACT_STATUS.SIGNED]: {
    label: 'Signed',
    color: 'bg-green-500',
    icon: CheckCircle,
  },
  [CONTRACT_STATUS.DEPOSIT_PAID]: {
    label: 'Deposit Paid',
    color: 'bg-cyan-500',
    icon: CreditCard,
  },
  [CONTRACT_STATUS.PAID]: {
    label: 'Fully Paid',
    color: 'bg-emerald-500',
    icon: DollarSign,
  },
};

export default function AdminContracts() {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    viewed: 0,
    signed: 0,
    deposit_paid: 0,
    paid: 0,
  });
  const [filter, setFilter] = useState('all');
  const [selectedContract, setSelectedContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [isCreatingPaymentLink, setIsCreatingPaymentLink] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    totalAmount: '',
    depositAmount: '',
    packageName: '',
    packageDuration: '',
    eventTime: '',
    venue: '',
    venueAddress: '',
    guestCount: '',
    loadInTime: '',
    soundCheckTime: '',
  });

  // Fetch contracts on mount
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const { contracts: contractsData, stats: statsData } =
          await getContractsWithStats();
        setContracts(contractsData);
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching contracts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContracts();
  }, []);

  const filteredContracts = contracts.filter((contract) => {
    if (filter === 'all') return true;
    return contract.status === filter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      let date;
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-').map(Number);
        date = new Date(y, m - 1, d);
      } else {
        date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr.toDate?.() || dateStr;
      }
      return format(date, 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount).toLocaleString()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBD';
    if (/AM|PM/i.test(timeStr)) return timeStr; // already 12h
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const handleDownloadPDF = async (contract) => {
    try {
      const blob = await pdf(
        <ContractTemplate
          contract={contract}
          signatureUrl={contract.signatureUrl}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.id?.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const copyContractLink = (contractId) => {
    const link = `${window.location.origin}/sign-contract/${contractId}`;
    navigator.clipboard.writeText(link);
    alert('Contract link copied to clipboard!');
  };

  const handleSendSMS = async () => {
    if (!selectedContract || !smsPhone) return;

    setIsSendingSMS(true);
    try {
      await sendContractSMS({
        phoneNumber: smsPhone,
        contractId: selectedContract.id,
        clientName: selectedContract.clientName,
        artistName: selectedContract.artistName,
      });

      // Update contract status to sent
      await updateContractStatus(selectedContract.id, CONTRACT_STATUS.SENT, {
        sentViaSMS: true,
        sentToPhone: smsPhone,
      });

      alert('SMS sent successfully!');
      setShowSMSModal(false);
      setSmsPhone('');

      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert(`Failed to send SMS: ${error.message}`);
    } finally {
      setIsSendingSMS(false);
    }
  };

  const handleCreatePaymentLink = async (contract) => {
    setIsCreatingPaymentLink(true);
    try {
      const result = await createPaymentLink({
        contractId: contract.id,
        artistName: contract.artistName,
        packageName: contract.packageName,
        eventDate: contract.eventDate,
        depositAmount: contract.depositAmount,
        clientEmail: contract.clientEmail,
      });

      // Update contract with payment link
      await updateContractStatus(contract.id, contract.status, {
        stripePaymentLink: result.paymentUrl,
        stripePaymentLinkId: result.paymentLinkId,
      });

      // Copy the payment link
      navigator.clipboard.writeText(result.paymentUrl);
      alert('Payment link created and copied to clipboard!');

      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error creating payment link:', error);
      alert(`Failed to create payment link: ${error.message}`);
    } finally {
      setIsCreatingPaymentLink(false);
    }
  };

  const handleEditContract = (contract) => {
    setEditForm({
      totalAmount: contract.totalAmount || '',
      depositAmount: contract.depositAmount || '',
      packageName: contract.packageName || '',
      packageDuration: contract.packageDuration || '',
      eventTime: contract.eventTime || '',
      venue: contract.venue || '',
      venueAddress: contract.venueAddress || '',
      guestCount: contract.guestCount || '',
      loadInTime: contract.loadInTime || '',
      soundCheckTime: contract.soundCheckTime || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedContract || !db) return;

    try {
      const contractRef = doc(db, 'contracts', selectedContract.id);
      await updateDoc(contractRef, {
        totalAmount: parseFloat(editForm.totalAmount) || selectedContract.totalAmount,
        depositAmount: parseFloat(editForm.depositAmount) || selectedContract.depositAmount,
        packageName: editForm.packageName || selectedContract.packageName,
        packageDuration: editForm.packageDuration || selectedContract.packageDuration,
        eventTime: editForm.eventTime || selectedContract.eventTime,
        venue: editForm.venue || selectedContract.venue,
        venueAddress: editForm.venueAddress || selectedContract.venueAddress,
        guestCount: editForm.guestCount || selectedContract.guestCount,
        loadInTime: editForm.loadInTime || null,
        soundCheckTime: editForm.soundCheckTime || null,
      });

      alert('Contract updated successfully!');
      setShowEditModal(false);
      
      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);
      
      // Update selected contract
      const updatedContract = contractsData.find(c => c.id === selectedContract.id);
      if (updatedContract) {
        setSelectedContract(updatedContract);
      }
    } catch (error) {
      console.error('Error updating contract:', error);
      alert(`Failed to update contract: ${error.message}`);
    }
  };

  const handleSendPaymentReminder = async (contract) => {
    if (!contract.stripePaymentLink) {
      alert('Please create a payment link first before sending a reminder.');
      return;
    }

    setIsSendingReminder(true);
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: contract.clientEmail,
          subject: `Payment Reminder: ${contract.artistName} Performance`,
          type: 'payment-reminder',
          customerName: contract.clientName,
          artistName: contract.artistName,
          venue: contract.venue || 'TBD',
          eventDate: formatDate(contract.eventDate),
          totalAmount: contract.totalAmount,
          depositAmount: contract.depositAmount,
          paymentLink: contract.stripePaymentLink,
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert('Payment reminder sent successfully!');
      } else {
        throw new Error(result.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      alert(`Failed to send payment reminder: ${error.message}`);
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleMarkDepositPaid = async (contract) => {
    if (!window.confirm('Mark this contract as deposit paid? This will update the status to "Deposit Paid".')) return;

    try {
      await updateContractStatus(contract.id, CONTRACT_STATUS.DEPOSIT_PAID);

      alert('Contract marked as deposit paid!');

      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);

      // Update selected contract if viewing
      if (selectedContract?.id === contract.id) {
        const updatedContract = contractsData.find(c => c.id === contract.id);
        if (updatedContract) setSelectedContract(updatedContract);
      }
    } catch (error) {
      console.error('Error marking deposit paid:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const handleCreateBalancePaymentLink = async (contract) => {
    // Validate amounts before creating payment link
    const total = Number(contract.totalAmount) || 0;
    const deposit = Number(contract.depositAmount) || 0;
    const balanceAmount = total - deposit;

    if (!balanceAmount || balanceAmount <= 0) {
      alert('Cannot create balance payment link: Invalid balance amount. Please check the contract total and deposit amounts.');
      return;
    }

    setIsCreatingPaymentLink(true);
    try {
      const result = await createPaymentLink({
        contractId: contract.id,
        artistName: contract.artistName,
        packageName: `${contract.packageName} - Final Payment`,
        eventDate: contract.eventDate,
        depositAmount: balanceAmount, // Using depositAmount field for the balance
        clientEmail: contract.clientEmail,
      });

      // Update contract with balance payment link
      await updateContractStatus(contract.id, contract.status, {
        stripeBalancePaymentLink: result.paymentUrl,
      });

      // Copy the payment link
      navigator.clipboard.writeText(result.paymentUrl);
      alert('Balance payment link created and copied to clipboard!');

      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);

      // Update selected contract if viewing
      if (selectedContract?.id === contract.id) {
        const updatedContract = contractsData.find(c => c.id === contract.id);
        if (updatedContract) setSelectedContract(updatedContract);
      }
    } catch (error) {
      console.error('Error creating balance payment link:', error);
      alert(`Failed to create balance payment link: ${error.message}`);
    } finally {
      setIsCreatingPaymentLink(false);
    }
  };

  const handleSendFinalPaymentReminder = async (contract) => {
    setIsSendingReminder(true);
    try {
      const balanceAmount = contract.totalAmount - contract.depositAmount;
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: contract.clientEmail,
          subject: `Final Payment Due: ${contract.artistName} Performance`,
          type: 'final-payment-reminder',
          customerName: contract.clientName,
          artistName: contract.artistName,
          venue: contract.venue || 'TBD',
          eventDate: formatDate(contract.eventDate),
          totalAmount: contract.totalAmount,
          depositAmount: contract.depositAmount,
          balanceAmount: balanceAmount,
          paymentLink: contract.stripeBalancePaymentLink || null,
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert('Final payment reminder sent successfully!');
      } else {
        throw new Error(result.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending final payment reminder:', error);
      alert(`Failed to send final payment reminder: ${error.message}`);
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleMarkFullyPaid = async (contract) => {
    if (!window.confirm('Mark this contract as fully paid? This will complete the payment process.')) return;

    try {
      await updateContractStatus(contract.id, CONTRACT_STATUS.PAID);

      alert('Contract marked as fully paid!');

      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);

      // Update selected contract if viewing
      if (selectedContract?.id === contract.id) {
        const updatedContract = contractsData.find(c => c.id === contract.id);
        if (updatedContract) setSelectedContract(updatedContract);
      }
    } catch (error) {
      console.error('Error marking fully paid:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract? This action cannot be undone.')) return;

    try {
      await deleteContract(contractId);

      // Close detail modal if viewing the deleted contract
      if (selectedContract?.id === contractId) {
        setSelectedContract(null);
      }

      // Refresh contracts
      const { contracts: contractsData, stats: statsData } = await getContractsWithStats();
      setContracts(contractsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Failed to delete contract');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Contracts</h2>
        <p className="text-gray-400">Manage performance agreements and track signatures</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card
          className={`cursor-pointer transition-all ${filter === 'all' ? 'border-orange-500' : ''}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {Object.entries(statusConfig).map(([status, config]) => (
          <Card
            key={status}
            className={`cursor-pointer transition-all ${filter === status ? `border-orange-500` : ''}`}
            onClick={() => setFilter(status)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${config.color}/20 rounded-lg`}>
                  <config.icon className={`w-5 h-5 ${config.color.replace('bg-', 'text-')}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats[status]}</p>
                  <p className="text-xs text-gray-400">{config.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contracts List */}
      <div className="space-y-4">
        {filteredContracts.map((contract) => {
          const statusInfo = statusConfig[contract.status] || statusConfig.draft;
          const StatusIcon = statusInfo.icon;

          return (
            <motion.div
              key={contract.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="hover:border-orange-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {/* Status Icon */}
                      <div className={`p-3 ${statusInfo.color}/20 rounded-lg`}>
                        <StatusIcon
                          className={`w-6 h-6 ${statusInfo.color.replace('bg-', 'text-')}`}
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-white">{contract.clientName}</h3>
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded text-white ${statusInfo.color}`}
                          >
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-orange-400 font-medium">
                          {contract.artistName} - {contract.packageName}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(contract.eventDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {formatCurrency(contract.totalAmount)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {contract.clientEmail}
                          </span>
                        </div>
                        {/* Progress Bar */}
                        <ContractProgressBar status={contract.status} />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 md:flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedContract(contract)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadPDF(contract)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyContractLink(contract.id)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {/* Signed: Create deposit payment link or send reminder */}
                      {contract.status === CONTRACT_STATUS.SIGNED && !contract.stripePaymentLink && (
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handleCreatePaymentLink(contract)}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Deposit Link
                        </Button>
                      )}
                      {contract.status === CONTRACT_STATUS.SIGNED && contract.stripePaymentLink && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendPaymentReminder(contract)}
                            disabled={isSendingReminder}
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 border-yellow-500/30"
                            title="Send deposit reminder"
                          >
                            {isSendingReminder ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Bell className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-cyan-500 hover:bg-cyan-600"
                            onClick={() => handleMarkDepositPaid(contract)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Deposit Paid
                          </Button>
                        </>
                      )}
                      {/* Deposit Paid: Create balance payment link, send final reminder, or mark fully paid */}
                      {contract.status === CONTRACT_STATUS.DEPOSIT_PAID && (
                        <>
                          {!contract.stripeBalancePaymentLink && (
                            <Button
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600"
                              onClick={() => handleCreateBalancePaymentLink(contract)}
                            >
                              <DollarSign className="w-4 h-4 mr-1" />
                              Balance Link
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendFinalPaymentReminder(contract)}
                            disabled={isSendingReminder}
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 border-orange-500/30"
                            title="Send final payment reminder"
                          >
                            {isSendingReminder ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Bell className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600"
                            onClick={() => handleMarkFullyPaid(contract)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Fully Paid
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteContract(contract.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}

        {filteredContracts.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No contracts found</h3>
            <p className="text-gray-400">
              {filter === 'all'
                ? 'Contracts will appear here when created from confirmed bookings'
                : `No ${statusConfig[filter]?.label.toLowerCase()} contracts`}
            </p>
          </div>
        )}
      </div>

      {/* Contract Detail Modal */}
      {selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Contract Details</h3>
                <p className="text-sm text-gray-400">
                  #{selectedContract.id?.substring(0, 8).toUpperCase()}
                </p>
              </div>
              <button
                onClick={() => setSelectedContract(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 text-sm font-semibold rounded text-white ${
                    statusConfig[selectedContract.status]?.color || 'bg-gray-500'
                  }`}
                >
                  {statusConfig[selectedContract.status]?.label || 'Unknown'}
                </span>
                {selectedContract.signedAt && (
                  <span className="text-sm text-gray-400">
                    Signed on {formatDate(selectedContract.signedAt)}
                  </span>
                )}
              </div>

              {/* Client Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                  Client Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <User className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Name</p>
                      <p className="text-white font-medium">{selectedContract.clientName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Mail className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Email</p>
                      <p className="text-white font-medium">{selectedContract.clientEmail}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                  Event Details
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-400">Date & Time</p>
                    <p className="text-white font-medium">
                      {formatDate(selectedContract.eventDate)} at{' '}
                      {formatTime(selectedContract.eventTime)}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-400">Venue</p>
                    <p className="text-white font-medium">
                      {selectedContract.venue || 'TBD'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Package & Pricing */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-400 uppercase">
                    Package & Pricing
                  </h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditContract(selectedContract)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="w-3 h-3" />
                    Edit Details
                  </Button>
                </div>
                <div className="bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-white font-semibold">
                      {selectedContract.packageName}
                    </span>
                    <span className="text-orange-500 font-bold text-lg">
                      {formatCurrency(selectedContract.totalAmount)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400">
                    <p>Artist: {selectedContract.artistName}</p>
                    <p>Duration: {selectedContract.packageDuration}</p>
                    {selectedContract.customDuration && (
                      <p className="text-orange-400">Custom: {selectedContract.customDuration} hours</p>
                    )}
                    <p>Deposit: {formatCurrency(selectedContract.depositAmount)}</p>
                  </div>
                </div>
              </div>

              {/* Signature */}
              {selectedContract.signatureUrl && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                    Client Signature
                  </h4>
                  <div className="bg-white rounded-lg p-4">
                    <img
                      src={selectedContract.signatureUrl}
                      alt="Client signature"
                      className="max-h-24 mx-auto"
                    />
                  </div>
                </div>
              )}

              {/* Contract Link */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                  Contract Link
                </h4>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/sign-contract/${selectedContract.id}`}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-300"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyContractLink(selectedContract.id)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(`/sign-contract/${selectedContract.id}`, '_blank')
                    }
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Send Actions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                  Send Contract
                </h4>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setSmsPhone(selectedContract.clientPhone || '');
                      setShowSMSModal(true);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send via SMS
                  </Button>
                  {selectedContract.status === CONTRACT_STATUS.SIGNED && (
                    <Button
                      onClick={() => handleCreatePaymentLink(selectedContract)}
                      disabled={isCreatingPaymentLink || selectedContract.stripePaymentLink}
                      className="flex-1 bg-green-500 hover:bg-green-600"
                    >
                      {isCreatingPaymentLink ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      {selectedContract.stripePaymentLink ? 'Deposit Link Created' : 'Create Deposit Link'}
                    </Button>
                  )}
                </div>
                {/* Deposit Payment Link */}
                {selectedContract.stripePaymentLink && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1">Deposit Payment Link:</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={selectedContract.stripePaymentLink}
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-300"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedContract.stripePaymentLink);
                          alert('Deposit payment link copied!');
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    {selectedContract.status === CONTRACT_STATUS.SIGNED && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendPaymentReminder(selectedContract)}
                          disabled={isSendingReminder}
                          className="text-yellow-400 hover:text-yellow-300"
                        >
                          <Bell className="w-4 h-4 mr-1" />
                          Send Deposit Reminder
                        </Button>
                        <Button
                          size="sm"
                          className="bg-cyan-500 hover:bg-cyan-600"
                          onClick={() => handleMarkDepositPaid(selectedContract)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Deposit Paid
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Balance Payment Section - Only for deposit_paid contracts */}
              {(selectedContract.status === CONTRACT_STATUS.DEPOSIT_PAID || selectedContract.stripeBalancePaymentLink) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                    Final Payment
                  </h4>
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-3">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Total Amount:</span>
                      <span className="text-white">{formatCurrency(selectedContract.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Deposit Paid:</span>
                      <span className="text-green-400">- {formatCurrency(selectedContract.depositAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold border-t border-gray-700 pt-2">
                      <span className="text-white">Balance Due:</span>
                      <span className="text-orange-400">{formatCurrency(selectedContract.totalAmount - selectedContract.depositAmount)}</span>
                    </div>
                  </div>
                  {selectedContract.status === CONTRACT_STATUS.DEPOSIT_PAID && (
                    <>
                      {!selectedContract.stripeBalancePaymentLink && (
                        <Button
                          onClick={() => handleCreateBalancePaymentLink(selectedContract)}
                          disabled={isCreatingPaymentLink}
                          className="w-full bg-orange-500 hover:bg-orange-600 mb-3"
                        >
                          {isCreatingPaymentLink ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          Create Balance Payment Link
                        </Button>
                      )}
                      {selectedContract.stripeBalancePaymentLink && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-400 mb-1">Balance Payment Link:</p>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={selectedContract.stripeBalancePaymentLink}
                              className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-300"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(selectedContract.stripeBalancePaymentLink);
                                alert('Balance payment link copied!');
                              }}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendFinalPaymentReminder(selectedContract)}
                          disabled={isSendingReminder}
                          className="flex-1 text-orange-400 hover:text-orange-300"
                        >
                          <Bell className="w-4 h-4 mr-1" />
                          Send Final Reminder
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                          onClick={() => handleMarkFullyPaid(selectedContract)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Fully Paid
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-gray-800">
                <Button
                  onClick={() => handleDownloadPDF(selectedContract)}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => handleDeleteContract(selectedContract.id)}
                  variant="outline"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* SMS Modal */}
      {showSMSModal && selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Send Contract via SMS</h3>
              <button
                onClick={() => {
                  setShowSMSModal(false);
                  setSmsPhone('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Phone Number
                </label>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <input
                    type="tel"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">Message Preview:</p>
                <p className="text-sm text-white">
                  Hi {selectedContract.clientName}! Your performance agreement from{' '}
                  {selectedContract.artistName} is ready to sign. Please review and sign here:{' '}
                  <span className="text-orange-400">[link]</span>
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSMSModal(false);
                    setSmsPhone('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendSMS}
                  disabled={!smsPhone || isSendingSMS}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600"
                >
                  {isSendingSMS ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send SMS
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Contract Modal */}
      {showEditModal && selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-2xl w-full p-6 my-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Contract Details</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* Pricing */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="totalAmount" className="text-gray-300">Total Amount ($)</Label>
                  <Input
                    id="totalAmount"
                    type="number"
                    step="0.01"
                    value={editForm.totalAmount}
                    onChange={(e) => setEditForm({ ...editForm, totalAmount: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="depositAmount" className="text-gray-300">Deposit Amount ($)</Label>
                  <Input
                    id="depositAmount"
                    type="number"
                    step="0.01"
                    value={editForm.depositAmount}
                    onChange={(e) => setEditForm({ ...editForm, depositAmount: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Package Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="packageName" className="text-gray-300">Package Name</Label>
                  <Input
                    id="packageName"
                    value={editForm.packageName}
                    onChange={(e) => setEditForm({ ...editForm, packageName: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="packageDuration" className="text-gray-300">Package Duration</Label>
                  <Input
                    id="packageDuration"
                    value={editForm.packageDuration}
                    onChange={(e) => setEditForm({ ...editForm, packageDuration: e.target.value })}
                    placeholder="e.g., 3 hours"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Event Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="eventTime" className="text-gray-300">Event Time</Label>
                  <Input
                    id="eventTime"
                    type="time"
                    value={editForm.eventTime}
                    onChange={(e) => setEditForm({ ...editForm, eventTime: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="guestCount" className="text-gray-300">Guest Count</Label>
                  <Input
                    id="guestCount"
                    type="number"
                    value={editForm.guestCount}
                    onChange={(e) => setEditForm({ ...editForm, guestCount: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Venue */}
              <div>
                <Label htmlFor="venue" className="text-gray-300">Venue Name</Label>
                <Input
                  id="venue"
                  value={editForm.venue}
                  onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="venueAddress" className="text-gray-300">Venue Address</Label>
                <Input
                  id="venueAddress"
                  value={editForm.venueAddress}
                  onChange={(e) => setEditForm({ ...editForm, venueAddress: e.target.value })}
                  className="mt-1"
                />
              </div>

              {/* Load In and Sound Check */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="loadInTime" className="text-gray-300">Load In Time</Label>
                  <Input
                    id="loadInTime"
                    type="time"
                    value={editForm.loadInTime}
                    onChange={(e) => setEditForm({ ...editForm, loadInTime: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="soundCheckTime" className="text-gray-300">Sound Check Deadline</Label>
                  <Input
                    id="soundCheckTime"
                    type="time"
                    value={editForm.soundCheckTime}
                    onChange={(e) => setEditForm({ ...editForm, soundCheckTime: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-6 mt-6 border-t border-gray-800">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-600"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
